import React, { useEffect, useState, useCallback } from 'react';
import { collection, getDocs, doc, updateDoc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../services/firebase/config';
import { migratePortfolioData, migratePositions } from '../utils/migrateData';
import { Switch } from '@headlessui/react';

function AdminPanel() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [migrationLoading, setMigrationLoading] = useState(false);
  const [alertMessage, setAlertMessage] = useState(null);
  const [registrationEnabled, setRegistrationEnabled] = useState(true);
  const [registrationStats, setRegistrationStats] = useState({
    attemptCount: 0,
    lastUpdated: null
  });
  const [registrationAttempts, setRegistrationAttempts] = useState([]);
  const [loadingStats, setLoadingStats] = useState(true);

  const showAlert = useCallback((type, message) => {
    setAlertMessage({ type, message });
    setTimeout(() => setAlertMessage(null), 5000);
  }, []);

  const fetchUsers = useCallback(async () => {
    try {
      const usersCollection = collection(db, 'users');
      const snapshot = await getDocs(usersCollection);
      const userData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setUsers(userData);
    } catch (error) {
      console.error('Error fetching users:', error);
      showAlert('error', 'Kullanıcılar yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  }, [showAlert]);

  const fetchRegistrationStatus = useCallback(async () => {
    try {
      const settingsDoc = doc(db, 'settings', 'registration');
      const docSnap = await getDoc(settingsDoc);
      
      if (docSnap.exists()) {
        setRegistrationEnabled(docSnap.data().enabled);
      } else {
        await setDoc(settingsDoc, { enabled: true });
      }
    } catch (error) {
      console.error('Registration status fetch error:', error);
      showAlert('error', 'Kayıt durumu yüklenirken hata oluştu');
    }
  }, [showAlert]);

  // Kayıt istatistiklerini çek
  const fetchRegistrationStats = useCallback(async () => {
    setLoadingStats(true);
    try {
      // Ana istatistikleri çek
      const statsRef = doc(db, 'stats', 'registration');
      const statsSnap = await getDoc(statsRef);
      
      if (statsSnap.exists()) {
        setRegistrationStats({
          attemptCount: statsSnap.data().attemptCount || 0,
          lastUpdated: statsSnap.data().lastUpdated
        });
      }

      // Son 20 kayıt girişimini çek
      const attemptsRef = collection(db, 'registrationAttempts');
      const attemptsSnap = await getDocs(attemptsRef);
      const attemptsList = attemptsSnap.docs
        .map(doc => ({
          id: doc.id,
          ...doc.data(),
          timestamp: doc.data().timestamp?.toDate()
        }))
        .sort((a, b) => b.timestamp - a.timestamp)
        .slice(0, 20);
      
      setRegistrationAttempts(attemptsList);
    } catch (error) {
      console.error('Registration stats fetch error:', error);
      showAlert('error', 'Kayıt istatistikleri yüklenirken hata oluştu');
    } finally {
      setLoadingStats(false);
    }
  }, [showAlert]);

  // Bekleme listesi istatistiklerini çekme fonksiyonu
  const fetchWaitlistStats = useCallback(async () => {
    setLoadingStats(true);
    try {
      // registrationAttempts koleksiyonundan verileri çek
      const attemptsRef = collection(db, 'registrationAttempts');
      const attemptsSnap = await getDocs(attemptsRef);
      
      // Tüm verileri çek
      const attemptsList = attemptsSnap.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        timestamp: doc.data().timestamp?.toDate()
      }))
      .sort((a, b) => b.timestamp - a.timestamp);
      
      // Kayıt sayısını ayarla
      setRegistrationStats({
        attemptCount: attemptsList.length,
        lastUpdated: attemptsList.length > 0 ? attemptsList[0].timestamp : null
      });
      
      // İlk 20 kayıdı göster
      setRegistrationAttempts(attemptsList.slice(0, 20));
    } catch (error) {
      console.error('Waitlist stats fetch error:', error);
      showAlert('error', 'Bekleme listesi yüklenirken hata oluştu');
    } finally {
      setLoadingStats(false);
    }
  }, [showAlert]);

  const toggleRegistration = async () => {
    try {
      const settingsDoc = doc(db, 'settings', 'registration');
      await updateDoc(settingsDoc, {
        enabled: !registrationEnabled
      });
      setRegistrationEnabled(!registrationEnabled);
      showAlert('success', `Kayıtlar ${!registrationEnabled ? 'açıldı' : 'kapatıldı'}`);
    } catch (error) {
      console.error('Registration toggle error:', error);
      showAlert('error', 'Kayıt durumu güncellenirken hata oluştu');
    }
  };

  useEffect(() => {
    fetchUsers();
    fetchRegistrationStatus();
    fetchWaitlistStats(); // Bekleme listesi istatistiklerini çek
  }, [fetchUsers, fetchRegistrationStatus, fetchWaitlistStats]);

  const handleUpdateStatus = async (userId, status) => {
    try {
      const userRef = doc(db, 'users', userId);
      await updateDoc(userRef, {
        membershipStatus: status,
        updatedAt: new Date().toISOString()
      });
      
      setUsers(users.map(user => 
        user.id === userId 
          ? {...user, membershipStatus: status}
          : user
      ));
      
      showAlert('success', 'Kullanıcı durumu güncellendi');
    } catch (error) {
      console.error('Error updating status:', error);
      showAlert('error', 'Kullanıcı durumu güncellenirken hata oluştu');
    }
  };

  const handleMigration = async () => {
    if (!window.confirm('Veri taşıma işlemini başlatmak istediğinize emin misiniz?')) {
      return;
    }

    setMigrationLoading(true);
    try {
      const adminEmail = 'zaferkekik@gmail.com';
      
      console.log('Portfolio verileri taşınıyor...', adminEmail);
      await migratePortfolioData(adminEmail);
      
      console.log('Pozisyon verileri taşınıyor...');
      await migratePositions(adminEmail);
      
      showAlert('success', 'Veriler başarıyla taşındı!');
    } catch (error) {
      console.error('Migration hatası:', error);
      showAlert('error', `Veri taşıma hatası: ${error.message}`);
    } finally {
      setMigrationLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      {alertMessage && (
        <div className={`mb-4 p-4 rounded-lg ${
          alertMessage.type === 'error' 
            ? 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-200' 
            : 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-200'
        }`}>
          {alertMessage.message}
        </div>
      )}

      <div className="bg-white dark:bg-[#151934] shadow rounded-lg p-6 mb-6">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Sistem İşlemleri</h2>
          <div className="space-x-4">
            <button
              onClick={handleMigration}
              disabled={migrationLoading}
              className={`bg-yellow-500 hover:bg-yellow-600 text-white px-4 py-2 rounded text-sm ${
                migrationLoading ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              {migrationLoading ? 'Veriler Taşınıyor...' : 'Veri Taşıma'}
            </button>
            <button
              onClick={fetchUsers}
              disabled={loading}
              className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded text-sm"
            >
              Listeyi Yenile
            </button>
          </div>
        </div>
      </div>

      <div className="bg-white dark:bg-[#151934] p-6 rounded-lg shadow-lg mb-6">
        <h2 className="text-xl font-semibold mb-4 dark:text-white">Kayıt Sistemi Kontrolü</h2>
        <div className="flex items-center justify-between mb-4">
          <span className="text-gray-700 dark:text-gray-300">
            Yeni Kayıtlar {registrationEnabled ? 'Açık' : 'Kapalı'}
          </span>
          <Switch
            checked={registrationEnabled}
            onChange={toggleRegistration}
            className={`${
              registrationEnabled ? 'bg-blue-600' : 'bg-gray-400'
            } relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none`}
          >
            <span
              className={`${
                registrationEnabled ? 'translate-x-6' : 'translate-x-1'
              } inline-block h-4 w-4 transform rounded-full bg-white transition-transform`}
            />
          </Switch>
        </div>
        
        {/* Kayıt İstatistikleri */}
        <div className="mt-6 border-t border-gray-200 dark:border-gray-700 pt-4">
          <h3 className="text-lg font-semibold mb-3 dark:text-white">Kayıt İstatistikleri</h3>
          
          {loadingStats ? (
            <div className="flex justify-center py-4">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
            </div>
          ) : (
            <>
              <div className="bg-blue-50 dark:bg-blue-900/30 rounded-lg p-4 mb-4">
                <div className="flex flex-col md:flex-row md:justify-between">
                  <div className="mb-2 md:mb-0">
                    <span className="text-gray-600 dark:text-gray-300">Bekleme listesindeki kullanıcı sayısı:</span>
                    <span className="ml-2 text-xl font-bold text-blue-600 dark:text-blue-400">{registrationStats.attemptCount}</span>
                  </div>
                  <div>
                    <span className="text-gray-500 dark:text-gray-400 text-sm">
                      Son güncelleme: {registrationStats.lastUpdated ? 
                        new Date(registrationStats.lastUpdated).toLocaleString('tr-TR') : '-'}
                    </span>
                  </div>
                </div>
              </div>
              
              {registrationAttempts.length > 0 && (
                <div className="mt-4">
                  <h4 className="text-md font-medium mb-2 dark:text-gray-200">Bekleme Listesi</h4>
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                      <thead className="bg-gray-50 dark:bg-[#1f2544]">
                        <tr>
                          <th scope="col" className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                            Email
                          </th>
                          <th scope="col" className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                            Tarih
                          </th>
                          <th scope="col" className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                            Platform
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white dark:bg-[#151934] divide-y divide-gray-200 dark:divide-gray-700">
                        {registrationAttempts.map((attempt) => (
                          <tr key={attempt.id}>
                            <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900 dark:text-gray-300">
                              {attempt.email || '-'}
                            </td>
                            <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                              {attempt.timestamp ? attempt.timestamp.toLocaleString('tr-TR') : '-'}
                            </td>
                            <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                              {attempt.platform || '-'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </>
          )}
          
          <div className="mt-4">
            <button
              onClick={fetchWaitlistStats}
              className="text-blue-600 dark:text-blue-400 text-sm hover:underline"
            >
              Bekleme Listesini Yenile
            </button>
          </div>
        </div>
      </div>

      <div className="bg-white dark:bg-[#151934] shadow rounded-lg p-6">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Kullanıcı Yönetimi</h2>
          <span className="text-sm text-gray-500 dark:text-gray-400">
            Toplam {users.length} kullanıcı
          </span>
        </div>
        
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-[#1f2544]">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Email
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Durum
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Son Güncelleme
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  İşlemler
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-[#151934] divide-y divide-gray-200 dark:divide-gray-700">
              {users.map((user) => (
                <tr key={user.id}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-300">
                    {user.email}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full
                      ${user.membershipStatus === 'active' 
                        ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' 
                        : user.membershipStatus === 'suspended'
                        ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                        : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'}`}>
                      {user.membershipStatus === 'active' ? 'Aktif' : 
                       user.membershipStatus === 'pending' ? 'Beklemede' : 
                       user.membershipStatus === 'suspended' ? 'Askıya Alındı' : 'Beklemede'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    {user.updatedAt ? new Date(user.updatedAt).toLocaleString('tr-TR') : '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                    <button
                      onClick={() => handleUpdateStatus(user.id, 'active')}
                      disabled={user.membershipStatus === 'active'}
                      className={`text-green-600 dark:text-green-400 hover:text-green-900 dark:hover:text-green-300 ${
                        user.membershipStatus === 'active' ? 'opacity-50 cursor-not-allowed' : ''
                      }`}
                    >
                      Aktifleştir
                    </button>
                    <button
                      onClick={() => handleUpdateStatus(user.id, 'suspended')}
                      disabled={user.membershipStatus === 'suspended'}
                      className={`text-red-600 dark:text-red-400 hover:text-red-900 dark:hover:text-red-300 ${
                        user.membershipStatus === 'suspended' ? 'opacity-50 cursor-not-allowed' : ''
                      }`}
                    >
                      Askıya Al
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default AdminPanel;